import type { LearningDevice } from 'definitions';
import { useLocation, useNavigate } from 'react-router';
import { TRACKED_DEVICE_TYPES } from 'definitions';

/** The three tabs, and the two pages under them (a pattern, a test's debrief). */
export const LEARNINGS_TABS = ['learnings', 'evidence', 'opportunities'] as const;
export type LearningsTab = (typeof LEARNINGS_TABS)[number];

export type LearningsView =
  | { screen: 'tab'; tab: LearningsTab }
  /** The foundation check, reached from Evidence once learnings are on; not a tab of its own. */
  | { screen: 'foundation' }
  | { screen: 'pattern'; patternKey: string }
  | { screen: 'test'; experimentId: number };

type ViewOfParams = { tab: string; pattern: string; test: number };

/** Unknown values fall back to the Learnings tab, so a stale link still lands somewhere. */
export const viewOf = ({ tab, pattern, test }: ViewOfParams): LearningsView => {
  if (test > 0) return { screen: 'test', experimentId: test };
  if (pattern) return { screen: 'pattern', patternKey: pattern };
  if (tab === 'foundation') return { screen: 'foundation' };
  return {
    screen: 'tab',
    tab: (LEARNINGS_TABS as readonly string[]).includes(tab) ? (tab as LearningsTab) : 'learnings',
  };
};

/** The view's keys; null removes a key, so a default never clutters the link. */
type ViewParams = { tab?: string | null; pattern?: string | null; test?: number | null; device?: string | null };

type WithParamsParams = { search: string; changes: ViewParams };

/**
 * The search string with the view's keys changed and everything else kept (the scope keys `org` and
 * `ws` among them). Pure, for the test.
 */
export const withParams = ({ search, changes }: WithParamsParams): string => {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined || value === '' || value === 0) params.delete(key);
    else params.set(key, String(value));
  }
  const next = params.toString();
  return next ? `?${next}` : '';
};

/**
 * Where the reader is on the Learnings page, in the query string so any view can be linked to:
 * `?tab=`, `?pattern=<key>` for a pattern, `?test=<id>` for a debrief, and `?device=`.
 *
 * Read from the router's location, and moved with a push, so Back steps back through the views (a
 * debrief to its pattern, a pattern to the feed) before it leaves the page. useQueryState replaces
 * the entry and keeps its own copy, which is right for a filter and wrong here: Back from a pattern
 * left Learnings altogether (reported 2026-10-20). The device is a filter, so it replaces.
 */
export const useLearningsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab') ?? 'learnings';
  const pattern = params.get('pattern') ?? '';
  const test = Number(params.get('test') ?? 0) || 0;
  const deviceParam = params.get('device') ?? '';

  /**
   * The section the reader is in, also on a pattern or a debrief opened from it, so the row's menu
   * and trail can say where they are. The foundation check is reached from Evidence.
   */
  const section: LearningsTab = (LEARNINGS_TABS as readonly string[]).includes(tab)
    ? (tab as LearningsTab)
    : tab === 'foundation'
      ? 'evidence'
      : 'learnings';

  const device: LearningDevice | null = (TRACKED_DEVICE_TYPES as readonly string[]).includes(deviceParam)
    ? (deviceParam as LearningDevice)
    : null;

  type MoveParams = { changes: ViewParams; replace?: boolean };
  // From window.location, not the router's: the scope pickers write `org` and `ws` with
  // replaceState (useQueryState), which the router never sees, so its copy can hold the old website.
  const move = ({ changes, replace = false }: MoveParams) =>
    navigate(
      {
        pathname: window.location.pathname,
        search: withParams({ search: window.location.search, changes }),
        hash: window.location.hash,
      },
      { replace },
    );

  const go = (next: { tab?: LearningsTab; pattern?: string; test?: number }) =>
    move({
      changes: {
        tab: next.tab && next.tab !== 'learnings' ? next.tab : null,
        pattern: next.pattern ?? null,
        test: next.test ?? null,
      },
    });

  return {
    view: viewOf({ tab, pattern, test }),
    section,
    /** The pattern a debrief was opened from, or the one shown; empty for none. */
    patternKey: pattern,
    device,
    setDevice: (value: LearningDevice | null) => move({ changes: { device: value }, replace: true }),
    openTab: (value: LearningsTab) => go({ tab: value }),
    openFoundation: () => move({ changes: { tab: 'foundation', pattern: null, test: null } }),
    // Both stay in the section they were opened from, so the trail reads "Evidence › #5871".
    openPattern: (patternKey: string) => go({ tab: section, pattern: patternKey }),
    // Keeps the pattern it was opened from, so the trail can step back to it.
    openTest: (experimentId: number) => go({ tab: section, pattern, test: experimentId }),
  };
};

export type LearningsScreenManager = ReturnType<typeof useLearningsScreen>;
